/**
 * Sending something often (a cursor, a dragged node) without flooding the core: at most one
 * call every few milliseconds, always ending with the latest value.
 */

/**
 * Calls `send` with the latest value at most once every `ms`: the first at once, the others
 * when the wait is over (only the last of them). `null` (leaving) is never delayed behind a
 * move.
 */
export function throttle<T>(ms: number, send: (value: T) => void, now: () => number = Date.now): (value: T) => void {
  let last = -Infinity;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let waiting: { value: T } | null = null;
  const flush = () => {
    timer = null;
    if (!waiting) return;
    last = now();
    const { value } = waiting;
    waiting = null;
    send(value);
  };
  return (value) => {
    waiting = { value };
    const wait = last + ms - now();
    if (wait <= 0 || value === null) {
      if (timer) clearTimeout(timer);
      flush();
    } else if (!timer) {
      timer = setTimeout(flush, wait);
    }
  };
}
